import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useUpdateShopWorkflowLogistician from "@babackapp/private-api-client/client/use-update-shop-workflow-logistician";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Form, FormField } from "@babackapp/ui/components/form";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

const ShopSettingsWorkflowFormSchema = z.object({
  workflowLogistician: z.boolean(),
});

export type ShopSettingsWorkflowFormValues = z.infer<
  typeof ShopSettingsWorkflowFormSchema
>;

export default function ShopSettingsWorkflowForm() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateWorkflowLogistician } =
    useUpdateShopWorkflowLogistician(shop.id);

  const planActivated = !!shop.plan.workflowLogistician;

  const form = useForm<ShopSettingsWorkflowFormValues>({
    mode: "onChange",
    defaultValues: {
      workflowLogistician: shop.workflowLogistician.activated,
    },
  });

  const values = useWatch({ control: form.control });

  const onSubmit = form.handleSubmit(
    ({ workflowLogistician }: ShopSettingsWorkflowFormValues) => {
      updateWorkflowLogistician(workflowLogistician).then(() =>
        form.reset({ workflowLogistician }),
      );
    },
  );

  useEffect(() => {
    if (form.formState.isDirty) onSubmit();
  }, [values, form.formState.isDirty]);

  const isAdmin = useViewerHasAtLeastAdminRights();

  const disabledBecausePlanNotActivated = planActivated
    ? false
    : t("settings.shop.errors.planNotActivated");

  const disabledBecauseNotAdmin = isAdmin
    ? false
    : t("settings.shop.errors.notAdmin");

  const switchIsDisabled =
    disabledBecauseNotAdmin || disabledBecausePlanNotActivated;

  return (
    <Form {...form}>
      <form>
        <FormField
          disabled={!isAdmin}
          control={form.control}
          name="workflowLogistician"
          render={({ field }) => (
            <FormSwitch
              label={t("settings.config.workflowLogistician.name")}
              description={t(
                "settings.config.workflowLogistician.fieldDescription",
              )}
              {...field}
              disabled={switchIsDisabled}
            />
          )}
        />
      </form>
    </Form>
  );
}
